import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Divider,
    Tabs,
    Tab,
} from '@mui/material';
import {
    List as ListIcon,
    Assessment as AssessmentIcon,
    TrendingUp as TrendingUpIcon,
    AddCard as AddCardIcon,
    CardGiftcard,
    LockReset as LockResetIcon,
} from '@mui/icons-material'; // Icons for tabs and heading
import { useNavigate } from 'react-router-dom';
import { giftcardService } from '../services/giftcardService';
import GiftcardFilter from '../components/GiftcardFilter';
import GiftCardTransactionReport from '../components/GiftCardTransactionReport';
import GiftcardPurchaseReport from '../components/GiftcardPurchaseReport';
import ResetPINModal from '../components/ResetPINModal';
import { useAuth } from '../contexts/AuthContext';

const GiftcardManagement = () => {
    const navigate = useNavigate();
    const { customerActivationEnabled } = useAuth();
    
    // State for tabs
    const [tabValue, setTabValue] = useState(0);
    
    // State for data
    const [giftcards, setGiftcards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [selectedGiftcard, setSelectedGiftcard] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [giftcardDetails, setGiftcardDetails] = useState(null);
    const [resetPINModalOpen, setResetPINModalOpen] = useState(false);
    
    // Transaction table sorting state
    const [transactionOrderBy, setTransactionOrderBy] = useState('TransactionDate');
    const [transactionOrder, setTransactionOrder] = useState('desc');
    
    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    
    // Sorting state
    const [orderBy, setOrderBy] = useState('GiftcardNo');
    const [order, setOrder] = useState('asc');

    // Load giftcards with pagination, sorting, and filters
    const loadGiftcards = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                pageSize: rowsPerPage,
                sortBy: orderBy,
                sortOrder: order,
                ...filters,
            };
            
            const response = await giftcardService.getGiftcards(params);
            setGiftcards(response.giftcards);
            setTotalCount(response.pagination.totalCount);
        } catch (error) {
            console.error('Error loading giftcards:', error);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, orderBy, order, filters]);

    useEffect(() => {
        loadGiftcards();
    }, [loadGiftcards]);

    // Handle sort request
    const handleRequestSort = (property) => () => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle transaction table sorting
    const handleTransactionSort = (property) => {
        const isAsc = transactionOrderBy === property && transactionOrder === 'asc';
        setTransactionOrder(isAsc ? 'desc' : 'asc');
        setTransactionOrderBy(property);
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Handle Reset PIN modal
    const handleOpenResetPINModal = () => {
        setResetPINModalOpen(true);
    };

    const handleCloseResetPINModal = () => {
        setResetPINModalOpen(false);
    };

    const handleResetPINSuccess = () => {
        // Optionally refresh the giftcard details or show a success message
        console.log('PIN reset successfully');
    };

    // Handle row click to show details
    const handleRowClick = async (giftcard) => {
        try {
            setSelectedGiftcard(giftcard);
            const details = await giftcardService.getGiftcardDetails(giftcard.GiftcardNo);
            setGiftcardDetails(details);
            setDetailDialogOpen(true);
        } catch (error) {
            console.error('Error loading giftcard details:', error);
        }
    };

    // Format date with null check and optional time
    // The date from SQL is already in UTC, display it as-is without timezone conversion
    const formatDate = (date, includeTime = false) => {
        if (!date) return '-';
        
        // Parse the date string manually to avoid timezone conversion
        let dateStr = date instanceof Date ? date.toISOString() : date;
        
        // Handle ISO string format (e.g., "2025-09-22T14:30:00.000Z")
        if (dateStr.includes('T')) {
            const [datePart, timePart] = dateStr.split('T');
            const [year, month, day] = datePart.split('-');
            
            if (includeTime) {
                // Extract time part and remove milliseconds/Z
                const time = timePart.replace(/\.\d{3}Z?$/, '').replace('Z', '');
                const [hours, minutes, seconds] = time.split(':');
                
                // Convert 24hr to 12hr format
                const hour24 = parseInt(hours);
                const ampm = hour24 >= 12 ? 'PM' : 'AM';
                const hour12 = hour24 % 12 || 12;
                
                return `${month}/${day}/${year}, ${hour12}:${minutes}:${seconds} ${ampm}`;
            }
            
            return `${month}/${day}/${year}`;
        }
        
        // Handle date-only format (e.g., "2025-09-22")
        const [year, month, day] = dateStr.split('-');
        return `${month}/${day}/${year}`;
    };

    // Format currency
    const formatCurrency = (amount) => {
        return amount !== null && amount !== undefined ? `$${amount.toFixed(2)}` : '-';
    };

    // Format phone number
    const formatPhoneNumber = (phone) => {
        if (!phone) return '-';
        // Remove all non-digit characters
        const cleaned = phone.replace(/\D/g, '');
        // Check if it's 10 digits
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        // Return original if not 10 digits
        return phone;
    };

    // Table columns configuration
    const columns = [
        { id: 'GiftcardNo', label: 'Giftcard No' },
        { id: 'Sold At', label: 'Sold At' },
        { id: 'SoldDate', label: 'Sold Date' },
        { id: 'Sold By', label: 'Sold By' },
        { id: 'ActivationDate', label: 'Activation Date' },
        { id: 'RemainingBalance', label: 'Remaining Balance' },
    ];

    return (
        <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CardGiftcard color="primary" />
                Giftcard Management
            </Typography>

            <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
                <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange} 
                    aria-label="giftcard management tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        '& .MuiTabs-scrollButtons': {
                            width: '48px',
                            height: '48px',
                            '&.Mui-disabled': {
                                opacity: 0.3,
                            },
                            '&:not(.Mui-disabled)': {
                                opacity: 1,
                                display: 'flex',
                            },
                        },
                        '& .MuiTabs-scrollButtons.Mui-disabled': {
                            opacity: 0.3,
                            display: 'flex',
                        },
                        '& .MuiTabs-scrollable': {
                            overflowX: 'auto',
                            overflowY: 'hidden',
                        },
                        '& .MuiTabs-flexContainer': {
                            minWidth: 'max-content',
                            flexWrap: 'nowrap',
                        },
                        '& .MuiTabs-indicator': {
                            display: 'block',
                        },
                        // Force scroll buttons to be visible when needed
                        '&.MuiTabs-scrollable': {
                            '& .MuiTabs-scrollButtons': {
                                display: 'flex !important',
                                visibility: 'visible !important',
                            },
                        },
                    }}
                >
                    <Tab 
                        label="Giftcard List" 
                        icon={<ListIcon />} 
                        iconPosition="start"
                    />
                    <Tab 
                        label="Transaction Report" 
                        icon={<AssessmentIcon />} 
                        iconPosition="start"
                    />
                    <Tab 
                        label="Giftcard Sold Report" 
                        icon={<TrendingUpIcon />} 
                        iconPosition="start"
                    />
                    {customerActivationEnabled && (
                        <Tab 
                            label="Activate Giftcard" 
                            icon={<AddCardIcon />} 
                            iconPosition="start"
                        />
                    )}
                </Tabs>
            </Paper>

            {tabValue === 0 && (
                <>
                    <GiftcardFilter onFilterChange={setFilters} />

            <Paper sx={{ width: '100%', mb: 2 }}>
                <TableContainer>
                    <Table size="medium">
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        sortDirection={orderBy === column.id ? order : false}
                                    >
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : 'asc'}
                                            onClick={handleRequestSort(column.id)}
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {giftcards.map((giftcard) => (
                                <TableRow 
                                    key={giftcard.GiftcardNo}
                                    hover
                                    onClick={() => handleRowClick(giftcard)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>{giftcard.GiftcardNo}</TableCell>
                                    <TableCell>{giftcard['Sold At']}</TableCell>
                                    <TableCell>{formatDate(giftcard.SoldDate)}</TableCell>
                                    <TableCell>{giftcard['Sold By']}</TableCell>
                                    <TableCell>{formatDate(giftcard.ActivationDate)}</TableCell>
                                    <TableCell>{formatCurrency(giftcard.RemainingBalance)}</TableCell>
                                </TableRow>
                            ))}
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        Loading...
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && giftcards.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No giftcards found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[25, 50, 100, 500]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
                </>
            )}

            {tabValue === 1 && (
                <GiftCardTransactionReport />
            )}

            {tabValue === 2 && (
                <GiftcardPurchaseReport />
            )}

            {tabValue === 3 && customerActivationEnabled && (
                <Box sx={{ p: 3 }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                        Activate Giftcard
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, maxWidth: 600 }}>
                        Click the "Start Activation" button below will bring you to the Customer Activation Form. 
                        Please note that after the customer finishes filling the form, the app will not navigate back to this site.
                    </Typography>
                    <Button 
                        variant="contained" 
                        size="large"
                        onClick={() => navigate('/customer-form')}
                        sx={{ mt: 2 }}
                    >
                        Start Activation
                    </Button>
                </Box>
            )}

            {/* Detail Dialog */}
            <Dialog
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography>
                            <strong>Giftcard Number: {giftcardDetails?.details.GiftcardNo}</strong>
                        </Typography>
                        <Box>
                            <Typography align="right">
                                Card Import Date: {formatDate(giftcardDetails?.details['Card Import Date'])}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {giftcardDetails && (
                        <Box sx={{ p: 2 }}>
                            {/* Giftcard Details */}
                            <Grid container spacing={3}>

                                <Grid xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                </Grid>

                                {/* Customer Information */}
                                <Grid xs={4}>
                                    <Box sx={{ p: 2, height: '100%' }}>
                                        <Typography variant="subtitle1" color="primary" gutterBottom>
                                            Customer Information
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                                                <Typography>{giftcardDetails.details['Customer Name']?.trim() || '-'}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary">Phone Number</Typography>
                                                <Typography>{formatPhoneNumber(giftcardDetails.details['Customer Phone'])}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                                                <Typography>{giftcardDetails.details['Customer Email'] || '-'}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>

                                {/* Giftcard Information */}
                                <Grid xs={4}>
                                    <Box sx={{ p: 2, height: '100%', borderLeft: 1, borderColor: 'divider' }}>
                                        <Typography variant="subtitle1" color="primary" gutterBottom>
                                            Giftcard Information
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary">Sold At</Typography>
                                                <Typography>{giftcardDetails.details['Sold At'] || '-'}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary">Sold Date</Typography>
                                                <Typography>
                                                    {formatDate(giftcardDetails.details.SoldDate)}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary">Sold By</Typography>
                                                <Typography>{giftcardDetails.details['Sold By'] || '-'}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>

                                {/* Activation Information */}
                                <Grid xs={4}>
                                    <Box sx={{ p: 2, height: '100%', borderLeft: 1, borderColor: 'divider' }}>
                                        <Typography variant="subtitle1" color="primary" gutterBottom>
                                            Activation Information
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary">Activated At</Typography>
                                                <Typography>{giftcardDetails.details['Activated At'] || '-'}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary">Activation Date</Typography>
                                                <Typography>
                                                    {formatDate(giftcardDetails.details.ActivationDate)}
                                                </Typography>
                                            </Box>
                                            {giftcardDetails.details.ActivationDate && (
                                                <Box>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<LockResetIcon />}
                                                        onClick={handleOpenResetPINModal}
                                                        sx={{ mt: 1 }}
                                                    >
                                                        Reset PIN
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>

                            </Grid>

                            {/* Remaining Balance */}
                            <Box sx={{ mt: 4, mb: 4, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="subtitle1" color="primary">Remaining Balance:</Typography>
                                    <Typography variant="h5" color="primary.main">{formatCurrency(giftcardDetails.details.RemainingBalance)}</Typography>
                                </Box>
                            </Box>

                            {/* Transaction History */}
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Transaction History
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={transactionOrderBy === 'POSTransactionNo'}
                                                        direction={transactionOrderBy === 'POSTransactionNo' ? transactionOrder : 'asc'}
                                                        onClick={() => handleTransactionSort('POSTransactionNo')}
                                                    >
                                                        Transaction No
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={transactionOrderBy === 'TransactionDate'}
                                                        direction={transactionOrderBy === 'TransactionDate' ? transactionOrder : 'asc'}
                                                        onClick={() => handleTransactionSort('TransactionDate')}
                                                    >
                                                        Date
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={transactionOrderBy === 'Amount'}
                                                        direction={transactionOrderBy === 'Amount' ? transactionOrder : 'asc'}
                                                        onClick={() => handleTransactionSort('Amount')}
                                                    >
                                                        Amount
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={transactionOrderBy === 'Store Name'}
                                                        direction={transactionOrderBy === 'Store Name' ? transactionOrder : 'asc'}
                                                        onClick={() => handleTransactionSort('Store Name')}
                                                    >
                                                        Store
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell>
                                                    <TableSortLabel
                                                        active={transactionOrderBy === 'WaiterLoginName'}
                                                        direction={transactionOrderBy === 'WaiterLoginName' ? transactionOrder : 'asc'}
                                                        onClick={() => handleTransactionSort('WaiterLoginName')}
                                                    >
                                                        Waiter
                                                    </TableSortLabel>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {giftcardDetails.transactions.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center">
                                                        No transactions found
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                [...giftcardDetails.transactions]
                                                    .sort((a, b) => {
                                                        const isAsc = transactionOrder === 'asc';
                                                        const aValue = a[transactionOrderBy];
                                                        const bValue = b[transactionOrderBy];
                                                        
                                                        if (transactionOrderBy === 'TransactionDate') {
                                                            return isAsc 
                                                                ? new Date(aValue) - new Date(bValue)
                                                                : new Date(bValue) - new Date(aValue);
                                                        }
                                                        
                                                        if (transactionOrderBy === 'Amount') {
                                                            return isAsc 
                                                                ? aValue - bValue
                                                                : bValue - aValue;
                                                        }
                                                        
                                                        return isAsc
                                                            ? aValue.localeCompare(bValue)
                                                            : bValue.localeCompare(aValue);
                                                    })
                                                    .map((transaction) => (
                                                        <TableRow key={transaction.POSTransactionNo}>
                                                            <TableCell>{transaction.POSTransactionNo}</TableCell>
                                                            <TableCell>{formatDate(transaction.TransactionDate, true)}</TableCell>
                                                            <TableCell>{formatCurrency(transaction.Amount)}</TableCell>
                                                            <TableCell>{transaction['Store Name']}</TableCell>
                                                            <TableCell>{transaction.WaiterLoginName}</TableCell>
                                                        </TableRow>
                                                    ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Reset PIN Modal */}
            <ResetPINModal
                open={resetPINModalOpen}
                onClose={handleCloseResetPINModal}
                giftcardNo={giftcardDetails?.details?.GiftcardNo}
                onSuccess={handleResetPINSuccess}
            />
        </Box>
    );
};

export default GiftcardManagement;
